import { AnyAction } from 'redux';
import { EffectsCommandMap, Subscription, SubscriptionAPI } from 'dva';
import { DvaReducer } from './shared';
import { ConnectState, UserModelState } from '@/models/connect';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import fMessage from '@/components/fMessage';
import userPermission from '@/permissions/UserPermission';
import { PolicyFullInfo_Type } from '@/type/contractTypes';
import fPolicyBuilder from '@/components/fPolicyBuilder';
import { history } from 'umi';
import moment from 'moment';
import {
  handleData_By_Sha1_And_ResourceTypeCode_And_InheritData,
  handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2,
} from '@/utils/service';
import {
  ICollectionCreateVersionDraftType,
  IResourceCreateVersionDraftType,
} from '@/type/resourceTypes';
import { FRegExpMgr } from '@/utils/FRegExpMgr';

export interface CollectionCreatorPageModelState {
  userInfo: {
    userID: number;
    userName: string;
  } | null;

  step: 1 | 2 | 3 | 4;

  step1_resourceType: {
    value: string;
    labels: string[];
    customInput?: string;
  } | null;
  step1_resourceType_errorText: string;
  step1_resourceTitle: string;
  step1_resourceTitle_errorText: string;
  step1_resourceName: string;
  step1_resourceName_optimized: string;
  step1_resourceName_isVerify: boolean;
  step1_resourceName_errorText: string;
  step1_createdResourceInfo: {
    resourceID: string;
    resourceName: string;
    resourceTypeCode: string;
    resourceType: string[];
  } | null;
  step1_dataIsDirty_count: number;

  step2_resourceTypeConfig: {
    uploadEntry: ('localUpload' | 'storageSpace' | 'markdownEditor' | 'cartoonEditor')[];
    limitFileSize: number;
    isSupportDownload: boolean;
    isSupportEdit: boolean;
    isSupportOptionalConfig: boolean;
  };

  step2_collectionItemsSetting: {
    collection_sort_list: 'collection_sort_ascending' | 'collection_sort_descending';
    collection_item_title:
      | 'collection_item_title_rtitle'
      | 'collection_item_title_sn'
      | 'collection_item_title_empty'
      | 'collection_item_title_custom';
    collection_item_no_display:
      | 'collection_item_no_display_show'
      | 'collection_item_no_display_hide';
    collection_item_image_display:
      | 'collection_item_image_display_show'
      | 'collection_item_image_display_hide';
    collection_item_descr_display:
      | 'collection_item_descr_display_show'
      | 'collection_item_descr_display_hide';
    collection_view: 'collection_view_list' | 'collection_view_card';
  };

  step2_resourceKeywords: string;
  step2_resources: {
    itemID: string;
    itemTitle: string;
    itemSerial: number;
    itemNumber: string;
    resourceInfo: {
      id: string;
      cover: string;
      name: string;
      title: string;
      version: string;
      policy: string[];
      type: string[];
      status: 0 | 1 | 2 | 4;
      authProblem: boolean;
      updateDate: string;
      username: string;
      useAvatar: string;
      isChoice: boolean;
      intro: string;
    };
  }[];
  step2_resources_state: 'loading' | 'noData' | 'noSearchResult' | 'loaded';
  step2_resources_totalCount: number;
  step2_resources_pageCurrent: number;
  step2_resources_pageSize: number;

  // step2_rawProperties: {
  //   key: string;
  //   name: string;
  //   value: string;
  //   description: string;
  // }[];
  // step2_additionalProperties: {
  //   key: string;
  //   name: string;
  //   value: string;
  //   description: string;
  // }[];
  step2_systemProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
    type: 'raw' | 'additional';
    valueConfig?: {
      text?: {
        nullable: boolean;
        minLength: number;
        maxLength: number;
      };
      textArea?: {
        nullable: boolean;
        minLength: number;
        maxLength: number;
      };
      integer?: {
        nullable: boolean;
        min: number;
        max: number;
      };
      decimal?: {
        nullable: boolean;
        minDecimal: number;
        maxDecimal: number;
        precision: number;
      };
      date?: {
        nullable: boolean;
        startDate: string;
        limitDate: string;
      };
      dataTime?: {
        nullable: boolean;
        startDateTime: string;
        limitDateTime: string;
      };
      configText?: {
        nullable: boolean;
        defaultValue: string;
        minLength: number;
        maxLength: number;
      };
      configEnum?: {
        nullable: boolean;
        defaultValue: string;
        options: string[];
      };
    };
  }[];
  step2_customProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];
  step2_customConfigurations: {
    key: string;
    name: string;
    description: string;
    type: 'input' | 'select';
    input: string;
    select: string[];
  }[];

  step2_directDependencies: {
    id: string;
    name: string;
    type: 'resource' | 'object';
    versionRange?: string;
  }[];
  step2_baseUpcastResources: {
    resourceID: string;
    resourceName: string;
  }[];
  step2_authExcludedItems: {
    resourceID: string;
    resourceName: string;
    contracts: {
      policyID: string;
      contractID: string;
    }[];
  }[];
  step2_isCompleteAuthorization: boolean;
  step2_authReload: number;

  step2_draftSaving: boolean;
  step2_draftSaveTime: string;
  step2_collectionItemsChanged: boolean;
  step2_otherChanged: boolean;

  step3_policies: PolicyFullInfo_Type[];
  step4_resourceCover: string;
  step4_resourceIntroduction: string;

  step4_updateSetting: {
    isFinish: boolean;
    automatic: boolean;
    conditionType: 'every' | 'some';
    conditions: {
      key: 'resourceTitle' | 'resourceTypeCode' | 'authIdentity';
      limitOperatorType:
        | 'INCLUDES'
        | 'NOT_INCLUDES'
        | 'STARTS_WITH'
        | 'ENDS_WITH'
        | 'EQUAL'
        | 'NOT_EQUAL';
      value: string;
      valueError: string;
    }[];
  };

  step4_resourceLabels: string[];
  step4_dataIsDirty_count: number;
}

export interface ChangeAction extends AnyAction {
  type: 'change' | 'collectionCreatorPage/change';
  payload: Partial<CollectionCreatorPageModelState>;
}

export interface OnMount_Page_Action extends AnyAction {
  type: 'collectionCreatorPage/onMount_Page';
}

export interface OnUnmount_Page_Action extends AnyAction {
  type: 'collectionCreatorPage/onUnmount_Page';
}

export interface OnChange_step1_resourceType_Action extends AnyAction {
  type: 'collectionCreatorPage/onChange_step1_resourceType';
  payload: {
    value: CollectionCreatorPageModelState['step1_resourceType'];
  };
}

export interface OnChange_step1_resourceTitle_Action extends AnyAction {
  type: 'collectionCreatorPage/onChange_step1_resourceTitle';
  payload: {
    value: CollectionCreatorPageModelState['step1_resourceTitle'];
  };
}

export interface OnChange_step1_resourceName_Action extends AnyAction {
  type: 'collectionCreatorPage/onChange_step1_resourceName';
  payload: {
    value: CollectionCreatorPageModelState['step1_resourceName'];
  };
}

export interface OnVerify_step1_resourceName_Action extends AnyAction {
  type: 'collectionCreatorPage/onVerify_step1_resourceName';
}

export interface OnClick_step1_createBtn_Action extends AnyAction {
  type: 'collectionCreatorPage/onClick_step1_createBtn';
}

export interface OnChange_step2_customProperties_Action extends AnyAction {
  type: 'collectionCreatorPage/onChange_step2_customProperties';
  payload: {
    value: CollectionCreatorPageModelState['step2_customProperties'];
  };
}

export interface OnChange_step2_resources_Action extends AnyAction {
  type: 'collectionCreatorPage/onChange_step2_resources';
  payload: {
    value: CollectionCreatorPageModelState['step2_resources'];
  };
}

export interface Step2_Fetch_CollectionItems_Action extends AnyAction {
  type: 'collectionCreatorPage/step2_Fetch_CollectionItems';
}

export interface OnChange_step2_systemProperties_Action extends AnyAction {
  type: 'collectionCreatorPage/onChange_step2_systemProperties';
  payload: {
    value: CollectionCreatorPageModelState['step2_systemProperties'];
  };
}

export interface OnChange_step2_customConfigurations_Action extends AnyAction {
  type: 'collectionCreatorPage/onChange_step2_customConfigurations';
  payload: {
    value: CollectionCreatorPageModelState['step2_customConfigurations'];
  };
}

export interface OnSync_Step2_properties_Action extends AnyAction {
  type: 'collectionCreatorPage/onSync_Step2_properties';
}

export interface Step2_SaveDraft_Action extends AnyAction {
  type: 'version_SaveDraft' | 'collectionCreatorPage/step2_SaveDraft';
}

export interface OnClick_step2_submitBtn_Action extends AnyAction {
  type: 'collectionCreatorPage/onClick_step2_submitBtn';
}

export interface OnClick_step3_addPolicyBtn_Action extends AnyAction {
  type: 'collectionCreatorPage/onClick_step3_addPolicyBtn';
  payload: {
    defaultValue?: { text: string; title: string };
  };
}

export interface OnClick_step3_submitBtn_Action extends AnyAction {
  type: 'collectionCreatorPage/onClick_step3_submitBtn';
}

export interface OnChange_step4_resourceCover_Action extends AnyAction {
  type: 'collectionCreatorPage/onChange_step4_resourceCover';
  payload: {
    value: CollectionCreatorPageModelState['step4_resourceCover'];
  };
}

export interface OnChange_step4_resourceLabels_Action extends AnyAction {
  type: 'collectionCreatorPage/onChange_step4_resourceLabels';
  payload: {
    value: CollectionCreatorPageModelState['step4_resourceLabels'];
  };
}

export interface Step4_Post_UpdateSetting_Action extends AnyAction {
  type: 'step4_Post_UpdateSetting' | 'collectionCreatorPage/step4_Post_UpdateSetting';
}

export interface OnClick_step4_preBtn_Action extends AnyAction {
  type: 'collectionCreatorPage/onClick_step4_preBtn';
}

export interface OnClick_step4_submitBtn_Action extends AnyAction {
  type: 'collectionCreatorPage/onClick_step4_submitBtn';
}

export interface ResourceCreatorPageModelType {
  namespace: 'collectionCreatorPage';
  state: CollectionCreatorPageModelState;
  effects: {
    onMount_Page: (action: OnMount_Page_Action, effects: EffectsCommandMap) => void;
    onUnmount_Page: (action: OnUnmount_Page_Action, effects: EffectsCommandMap) => void;
    onChange_step1_resourceType: (
      action: OnChange_step1_resourceType_Action,
      effects: EffectsCommandMap,
    ) => void;
    onChange_step1_resourceTitle: (
      action: OnChange_step1_resourceTitle_Action,
      effects: EffectsCommandMap,
    ) => void;
    onChange_step1_resourceName: (
      action: OnChange_step1_resourceName_Action,
      effects: EffectsCommandMap,
    ) => void;
    onVerify_step1_resourceName: (
      action: OnVerify_step1_resourceName_Action,
      effects: EffectsCommandMap,
    ) => void;
    onClick_step1_createBtn: (
      action: OnClick_step1_createBtn_Action,
      effects: EffectsCommandMap,
    ) => void;
    onChange_step2_resources: (
      action: OnChange_step2_resources_Action,
      effects: EffectsCommandMap,
    ) => void;
    step2_Fetch_CollectionItems: (
      action: Step2_Fetch_CollectionItems_Action,
      effects: EffectsCommandMap,
    ) => void;
    onChange_step2_systemProperties: (
      action: OnChange_step2_systemProperties_Action,
      effects: EffectsCommandMap,
    ) => void;
    onChange_step2_customProperties: (
      action: OnChange_step2_customProperties_Action,
      effects: EffectsCommandMap,
    ) => void;
    onChange_step2_customConfigurations: (
      action: OnChange_step2_customConfigurations_Action,
      effects: EffectsCommandMap,
    ) => void;
    onSync_Step2_properties: (
      action: OnSync_Step2_properties_Action,
      effects: EffectsCommandMap,
    ) => void;
    step2_SaveDraft: (action: Step2_SaveDraft_Action, effects: EffectsCommandMap) => void;
    onClick_step2_submitBtn: (
      action: OnClick_step2_submitBtn_Action,
      effects: EffectsCommandMap,
    ) => void;

    onClick_step3_addPolicyBtn: (
      action: OnClick_step3_addPolicyBtn_Action,
      effects: EffectsCommandMap,
    ) => void;
    onClick_step3_submitBtn: (
      action: OnClick_step3_submitBtn_Action,
      effects: EffectsCommandMap,
    ) => void;

    onChange_step4_resourceCover: (
      action: OnChange_step4_resourceCover_Action,
      effects: EffectsCommandMap,
    ) => void;
    onChange_step4_resourceLabels: (
      action: OnChange_step4_resourceLabels_Action,
      effects: EffectsCommandMap,
    ) => void;
    onClick_step4_preBtn: (action: OnClick_step4_preBtn_Action, effects: EffectsCommandMap) => void;
    step4_Post_UpdateSetting: (
      action: Step4_Post_UpdateSetting_Action,
      effects: EffectsCommandMap,
    ) => void;
    onClick_step4_submitBtn: (
      action: OnClick_step4_submitBtn_Action,
      effects: EffectsCommandMap,
    ) => void;
  };
  reducers: {
    change: DvaReducer<CollectionCreatorPageModelState, ChangeAction>;
  };
  subscriptions: { setup: Subscription };
}

export const initStates: CollectionCreatorPageModelState = {
  userInfo: null,

  step: 1,

  step1_resourceType: null,
  step1_resourceType_errorText: '',
  step1_resourceTitle: '',
  step1_resourceTitle_errorText: '',
  step1_resourceName: '',
  step1_resourceName_optimized: '',
  step1_resourceName_isVerify: false,
  step1_resourceName_errorText: '',
  step1_createdResourceInfo: null,
  step1_dataIsDirty_count: 0,

  step2_resourceTypeConfig: {
    uploadEntry: [],
    limitFileSize: 0,
    isSupportDownload: false,
    isSupportEdit: false,
    isSupportOptionalConfig: false,
  },
  step2_collectionItemsSetting: {
    collection_sort_list: 'collection_sort_ascending',
    collection_item_title: 'collection_item_title_rtitle',
    collection_item_no_display: 'collection_item_no_display_show',
    collection_item_image_display: 'collection_item_image_display_show',
    collection_item_descr_display: 'collection_item_descr_display_show',
    collection_view: 'collection_view_list',
  },
  step2_resourceKeywords: '',
  step2_resources: [],
  step2_resources_state: 'noData',
  step2_resources_totalCount: 0,
  step2_resources_pageCurrent: 1,
  step2_resources_pageSize: 10,
  step2_systemProperties: [],
  step2_customProperties: [],
  step2_customConfigurations: [],

  step2_directDependencies: [],
  step2_baseUpcastResources: [],
  step2_authExcludedItems: [],
  step2_isCompleteAuthorization: true,
  step2_authReload: 0,

  step2_draftSaving: false,
  step2_draftSaveTime: '',
  step2_collectionItemsChanged: false,
  step2_otherChanged: false,

  step3_policies: [],

  step4_resourceCover: '',
  step4_resourceIntroduction: '',
  step4_updateSetting: {
    isFinish: false,
    automatic: false,
    conditionType: 'every',
    conditions: [],
  },
  step4_resourceLabels: [],
  step4_dataIsDirty_count: 0,
};

const Model: ResourceCreatorPageModelType = {
  namespace: 'collectionCreatorPage',
  state: initStates,
  effects: {
    *onMount_Page({}: OnMount_Page_Action, { call, put }: EffectsCommandMap) {
      const data: UserModelState['info'] = yield call(userPermission.getUserInfo);
      // console.log(data, 'dataisdjflksdjflkdsjlkj');
      if (!data) {
        return;
      }
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          userInfo: {
            userID: data.userId,
            userName: data.username,
          },
        },
      });
    },
    *onUnmount_Page({}: OnUnmount_Page_Action, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          ...initStates,
        },
      });
    },
    *onChange_step1_resourceType(
      { payload }: OnChange_step1_resourceType_Action,
      { select, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step1_resourceType: payload.value,
          step1_dataIsDirty_count: collectionCreatorPage.step1_dataIsDirty_count + 1,
          step1_resourceType_errorText:
            payload.value === null
              ? FI18n.i18nNext.t('naming_convention_resource_type_required')
              : '',
        },
      });
    },
    *onChange_step1_resourceName(
      { payload }: OnChange_step1_resourceName_Action,
      { select, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step1_resourceName: payload.value,
          step1_resourceName_optimized: FRegExpMgr.resourceNameOptimized(payload.value),
          // payload.value.replace(new RegExp(/(\\|\/|:|\*|\?|"|<|>|\||\s|@|\$|#)+/, 'g'), '_'),
          step1_resourceName_errorText: '',
          step1_resourceName_isVerify: true,
          step1_dataIsDirty_count: collectionCreatorPage.step1_dataIsDirty_count + 1,
        },
      });
    },
    *onVerify_step1_resourceName(
      {}: OnVerify_step1_resourceName_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );
      let nameErrorText: string = '';
      if (collectionCreatorPage.step1_resourceName_optimized === '') {
        // nameErrorText = '请输入资源授权标识';
        nameErrorText = FI18n.i18nNext.t('naming_convention_resource_authid_required');
      // } else if (collectionCreatorPage.step1_resourceName.length > 60) {
      //   nameErrorText = '不多于60个字符';
      } else {
        const params1: Parameters<typeof FServiceAPI.Resource.info>[0] = {
          resourceIdOrName: encodeURIComponent(
            `${collectionCreatorPage.userInfo?.userName}/${collectionCreatorPage.step1_resourceName_optimized}`,
          ),
        };
        const { data: data_info } = yield call(FServiceAPI.Resource.info, params1);
        if (!!data_info) {
          // nameErrorText = '资源授权标识已存在';
          nameErrorText = FI18n.i18nNext.t('resource_name_exist', {
            authID: collectionCreatorPage.step1_resourceName_optimized,
          });
        }
      }

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step1_resourceName_isVerify: false,
          step1_resourceName_errorText: nameErrorText,
        },
      });
    },
    *onClick_step1_createBtn(
      {}: OnClick_step1_createBtn_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage } = yield select(({ collectionCreatorPage }: ConnectState) => ({
        collectionCreatorPage,
      }));
      if (collectionCreatorPage.resourceTypeCodes === null) {
        return;
      }

      const params: Parameters<typeof FServiceAPI.Resource.create>[0] = {
        name: collectionCreatorPage.step1_resourceName_optimized,
        resourceTitle: collectionCreatorPage.step1_resourceTitle.trim(),
        resourceTypeCode: collectionCreatorPage.step1_resourceType.value,
        resourceTypeName: collectionCreatorPage.step1_resourceType.customInput || undefined,
        subjectType: 4,
        // intro: collectionCreatorPage.step4_resourceIntroduction,
      };
      const {
        ret,
        errCode,
        msg,
        data,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: {
          resourceId: string;
          resourceName: string;
          resourceType: string[];
          resourceTypeCode: string;
        };
      } = yield call(FServiceAPI.Resource.create, params);
      // console.log(data, 'dataiosdjflkjsdlkfjlksdjlk');
      if (ret !== 0 || errCode !== 0 || !data) {
        // self._czc?.push(['_trackEvent', '资源创建页', '创建资源', '', 0]);
        // fMessage('资源创建失败', 'error');
        fMessage(msg, 'error');
        return;
      }
      // self._czc?.push(['_trackEvent', '资源创建页', '创建资源', '', 1]);

      const params1: Parameters<typeof FServiceAPI.Resource.getResourceTypeInfoByCode>[0] = {
        code: collectionCreatorPage.step1_resourceType.value,
      };
      const {
        data: data_ResourceTypeInfo,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: {
          resourceConfig: {
            fileCommitMode: number[];
            fileMaxSize: number;
            fileMaxSizeUnit: 1 | 2;
            supportDownload: 1 | 2;
            supportEdit: 1 | 2;
            supportOptionalConfig: 1 | 2;
          };
        };
      } = yield call(FServiceAPI.Resource.getResourceTypeInfoByCode, params1);
      // console.log(data_ResourceTypeInfo, 'data_ResourceTypeInfo sdiofjsdlkjflksdjlkjl');
      const uploadEntry: CollectionCreatorPageModelState['step2_resourceTypeConfig']['uploadEntry'] =
        [];
      if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 0)) {
        uploadEntry.push('localUpload');
      }
      if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 1)) {
        uploadEntry.push('storageSpace');
      }
      if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 2)) {
        uploadEntry.push('markdownEditor');
      }
      if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 3)) {
        uploadEntry.push('cartoonEditor');
      }

      const params4: Parameters<typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData>[0] =
        {
          sha1: '',
          resourceTypeCode: collectionCreatorPage.step1_resourceType.value,
          inheritData: {
            additionalProperties: [],
            customProperties: [],
            customConfigurations: [],
          },
        };
      const result: Awaited<
        ReturnType<typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2>
      > = yield call(handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2, params4);
      // console.log(result, 'result sdifjsdlikfjlskdjfoiewsoi9jflksdjflksdjlk');

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step: 2,
          step1_createdResourceInfo: {
            resourceID: data.resourceId,
            resourceName: data.resourceName,
            resourceType: data.resourceType,
            resourceTypeCode: data.resourceTypeCode,
          },
          step1_dataIsDirty_count: 0,
          step2_resourceTypeConfig: {
            uploadEntry: uploadEntry,
            limitFileSize:
              data_ResourceTypeInfo.resourceConfig.fileMaxSize *
              1024 *
              1024 ** data_ResourceTypeInfo.resourceConfig.fileMaxSizeUnit,
            isSupportDownload: data_ResourceTypeInfo.resourceConfig.supportDownload === 2,
            isSupportEdit: data_ResourceTypeInfo.resourceConfig.supportEdit === 2,
            isSupportOptionalConfig:
              data_ResourceTypeInfo.resourceConfig.supportOptionalConfig === 2,
          },
          // step4_resourceTitle: collectionCreatorPage.step1_resourceName,
          // step2_rawProperties: result.rawProperties,
          // step2_additionalProperties: result.additionalProperties,
          step2_systemProperties: result.systemProperties,
          step2_customProperties: result.customProperties,
          step2_customConfigurations: result.customConfigurations,
        },
      });
    },
    *onChange_step2_resources(
      { payload }: OnChange_step2_resources_Action,
      { put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step2_resources: payload.value,
        },
      });
    },
    // TODO: 拉取草稿
    *step2_Fetch_CollectionItems(
      {}: Step2_Fetch_CollectionItems_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );

      let data_collectionItems: {
        createDate: string;
        itemId: string;
        itemTitle: string;
        sortId: number;
        number: string;
        mountResourceInfo: {
          resourceId: string;
        };
      }[] = [];

      const parmas: Parameters<typeof FServiceAPI.Resource.getCollectionItems_Draft>[0] = {
        resourceId: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        skip:
          (collectionCreatorPage.step2_resources_pageCurrent - 1) *
          collectionCreatorPage.step2_resources_pageSize,
        limit: collectionCreatorPage.step2_resources_pageSize,
        keywords: collectionCreatorPage.step2_resourceKeywords,
        // sortType: (collectionCreatorPage.step2_collectionItemsSetting.collection_sorting === 'collection_sort_newest' ||
        //   collectionCreatorPage.step2_collectionItemsSetting.collection_sorting === 'collection_sort_title_reverse' ||
        //   collectionCreatorPage.step2_collectionItemsSetting.collection_sorting === 'collection_sort_manual_reverse') ? -1 : 1,
        sortType:
          collectionCreatorPage.step2_collectionItemsSetting.collection_sort_list ===
          'collection_sort_descending'
            ? -1
            : 1,
        isLoadLatestVersionInfo: 1,
      };

      const {
        ret,
        errCode,
        data,
      }: {
        ret: number;
        errCode: number;
        data: {
          skip: number;
          limit: number;
          totalItem: number;
          dataList: {
            createDate: string;
            itemId: string;
            itemTitle: string;
            sortId: number;
            number: string;
            mountResourceInfo: {
              resourceId: string;
            };
          }[];
        };
      } = yield call(FServiceAPI.Resource.getCollectionItems_Draft, parmas);

      data_collectionItems = data.dataList;
      if (
        data.dataList.length === 0 &&
        collectionCreatorPage.step2_resources_pageCurrent > 1 &&
        data.totalItem > 0
      ) {
        const step2_resources_pageCurrent: number =
          Math.floor((data.totalItem - 1) / collectionCreatorPage.step2_resources_pageSize) + 1;

        yield put<ChangeAction>({
          type: 'change',
          payload: {
            step2_resources_pageCurrent: step2_resources_pageCurrent,
          },
        });

        const parmas: Parameters<typeof FServiceAPI.Resource.getCollectionItems_Draft>[0] = {
          resourceId: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
          // skip: (collectionCreatorPage.step2_resources_pageCurrent - 1) * collectionCreatorPage.step2_resources_pageSize,
          skip: (step2_resources_pageCurrent - 1) * collectionCreatorPage.step2_resources_pageSize,
          limit: collectionCreatorPage.step2_resources_pageSize,
          keywords: collectionCreatorPage.step2_resourceKeywords,
          // sortType: (collectionCreatorPage.step2_collectionItemsSetting.collection_sorting === 'collection_sort_newest' ||
          //   collectionCreatorPage.step2_collectionItemsSetting.collection_sorting === 'collection_sort_title_reverse' ||
          //   collectionCreatorPage.step2_collectionItemsSetting.collection_sorting === 'collection_sort_manual_reverse') ? -1 : 1,
          sortType:
            collectionCreatorPage.step2_collectionItemsSetting.collection_sort_list ===
            'collection_sort_descending'
              ? -1
              : 1,
        };

        const {
          ret,
          errCode,
          data: data1,
        }: {
          ret: number;
          errCode: number;
          data: {
            skip: number;
            limit: number;
            totalItem: number;
            dataList: {
              createDate: string;
              itemId: string;
              itemTitle: string;
              sortId: number;
              number: string;
              mountResourceInfo: {
                resourceId: string;
              };
            }[];
          };
        } = yield call(FServiceAPI.Resource.getCollectionItems_Draft, parmas);

        data_collectionItems = data1.dataList;
      }

      let data_resourceList: any[] = [];
      let auths: {
        itemId: string;
        isAuth: boolean;
      }[] = [];
      if (data_collectionItems.length > 0) {
        const { data: data_resourceList1 } = yield call(FServiceAPI.Resource.batchInfo, {
          resourceIds: data_collectionItems
            .map((d) => {
              return d.mountResourceInfo.resourceId;
            })
            .join(','),
        });
        data_resourceList = data_resourceList1;

        const parmas1: Parameters<typeof FServiceAPI.Resource.getCollectionItemsAuth_Draft>[0] = {
          resourceId: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
          itemIds: data_collectionItems
            .map((d) => {
              return d.itemId;
            })
            .join(','),
        };
        const {
          data: data1,
        }: {
          data: {
            itemId: string;
            isAuth: boolean;
          }[];
        } = yield call(FServiceAPI.Resource.getCollectionItemsAuth_Draft, parmas1);
        // console.log(data1, '********************************* sdoifjsadlkfjlkj');
        auths = data1;
      }

      //
      const list: CollectionCreatorPageModelState['step2_resources'] = data_collectionItems.map<
        CollectionCreatorPageModelState['step2_resources'][number]
      >((i, j: number) => {
        const resAuth = auths.find((dd) => {
          return dd.itemId === i.itemId;
        });
        // console.log(data_collectionItems[j], ' data.dataList[j] sdfsdfdsfsdf');
        const resource = data_resourceList.find((r) => {
          return r.resourceId === i.mountResourceInfo.resourceId;
        });
        return {
          itemID: i.itemId,
          itemTitle: i.itemTitle,
          itemSerial: i.sortId,
          itemNumber: i.number,
          resourceInfo: {
            id: resource.resourceId,
            cover: resource.coverImages.length > 0 ? resource.coverImages[0] : '',
            name: resource.resourceName,
            title: resource.resourceTitle,
            version: resource.latestVersion,
            policy: resource.policies
              .filter((l: any) => {
                return l.status === 1;
              })
              .map((l: any) => l.policyName),
            type: resource.resourceType,
            status: resource.status,
            authProblem: !!resAuth && !resAuth.isAuth,
            updateDate: moment(resource.updateDate).format('YYYY-MM-DD'),
            username: resource.username,
            useAvatar: `https://image.freelog.com/avatar/${resource.userId}`,
            isChoice: resource.operationType === 5,
            intro: resource.intro,
          },
        };
      });

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step2_resources: list,
          step2_resources_totalCount: data.totalItem,
          step2_resources_state:
            data.totalItem > 0
              ? 'loaded'
              : collectionCreatorPage.step2_resourceKeywords === ''
              ? 'noData'
              : 'noSearchResult',
        },
      });
    },
    *onChange_step2_systemProperties(
      { payload }: OnChange_step2_systemProperties_Action,
      { select, put }: EffectsCommandMap,
    ) {
      // const { collectionCreatorPage }: ConnectState = yield select(
      //   ({ collectionCreatorPage }: ConnectState) => ({
      //     collectionCreatorPage,
      //   }),
      // );

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          // step2_additionalProperties: payload.value,
          // step2_dataIsDirty_count: collectionCreatorPage.step2_dataIsDirty_count + 1,
          step2_systemProperties: payload.value,
        },
      });
    },
    *onChange_step2_customProperties(
      { payload }: OnChange_step2_customProperties_Action,
      { select, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step2_customProperties: payload.value,
          // step2_dataIsDirty_count: collectionCreatorPage.step2_dataIsDirty_count + 1,
        },
      });
    },
    *onChange_step2_customConfigurations(
      { payload }: OnChange_step2_customConfigurations_Action,
      { select, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step2_customConfigurations: payload.value,
          // step2_dataIsDirty_count: collectionCreatorPage.step2_dataIsDirty_count + 1,
        },
      });
    },
    *onSync_Step2_properties(
      {}: OnSync_Step2_properties_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );

      const authExcludedItems: {
        resourceId: string;
        excludedType: 'contractId' | 'policyId';
        excludedValue: string;
      }[] = collectionCreatorPage.step2_authExcludedItems.flatMap((r0) => {
        return r0.contracts.map((r1) => {
          return {
            resourceId: r0.resourceID,
            excludedType: 'contractId' as const,
            excludedValue: r1.contractID,
          };
        });
      });

      const params: Parameters<typeof FServiceAPI.Resource.updateCollection>[0] = {
        resourceId: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        authExcludedItems: authExcludedItems,
        // authExcludedItems: collectionCreatorPage.step2_authExcludedItems.map<
        //   NonNullable<
        //     Parameters<
        //       typeof FServiceAPI.Resource.updateCollection
        //     >[0]['authExcludedItems']
        //   >[number]
        // >((i) => {
        //   return {
        //     resourceId: i.resourceID,
        //     excludedType: 'policyId',
        //     excludedValue: i.contracts[0].policyID,
        //   };
        // }),
        customPropertyDescriptors: [
          ...collectionCreatorPage.step2_customProperties.map<
            NonNullable<
              Parameters<typeof FServiceAPI.Resource.createVersion>[0]['customPropertyDescriptors']
            >[number]
          >((i) => {
            return {
              type: 'readonlyText',
              key: i.key,
              name: i.name,
              remark: i.description,
              defaultValue: i.value,
            };
          }),
          ...collectionCreatorPage.step2_customConfigurations.map<
            NonNullable<
              Parameters<typeof FServiceAPI.Resource.createVersion>[0]['customPropertyDescriptors']
            >[number]
          >((i) => {
            const isInput: boolean = i.type === 'input';
            const options: string[] = i.select;
            return {
              type: isInput ? 'editableText' : 'select',
              key: i.key,
              name: i.name,
              remark: i.description,
              defaultValue: isInput ? i.input : options[0],
              // defaultValue: isInput ? i.input : '',
              candidateItems: isInput ? undefined : options,
            };
          }),
        ],
      };

      const {
        ret,
        errCode,
        msg,
        data: data_result,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: any;
      } = yield call(FServiceAPI.Resource.updateCollection, params);

      if (ret !== 0 || errCode !== 0) {
        fMessage(msg, 'error');
      }
    },
    *step2_SaveDraft({}: Step2_SaveDraft_Action, { select, call, put }: EffectsCommandMap) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step2_draftSaving: true,
        },
      });

      const params1: Parameters<typeof FServiceAPI.Resource.lookDraft>[0] = {
        resourceId: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
      };
      const {
        data: data_draft1,
      }: {
        data: null | {
          resourceId: string;
          updateDate: string;
          draftData: IResourceCreateVersionDraftType;
        };
      } = yield call(FServiceAPI.Resource.lookDraft, params1);

      const draftData: ICollectionCreateVersionDraftType = {
        ...(data_draft1?.draftData || {}),
        collectionItemsSetting: collectionCreatorPage.step2_collectionItemsSetting,
        additionalProperties: collectionCreatorPage.step2_systemProperties
          .filter((sp) => sp.type === 'additional')
          .map((sp) => {
            return {
              key: sp.key,
              value: sp.value,
            };
          }),
        customProperties: collectionCreatorPage.step2_customProperties,
        customConfigurations: collectionCreatorPage.step2_customConfigurations,
        directDependencies: collectionCreatorPage.step2_directDependencies,
        baseUpcastResources: collectionCreatorPage.step2_baseUpcastResources,
        descriptionEditorInput: '',
        collectionItemsChanged: collectionCreatorPage.step2_collectionItemsChanged,
        otherChanged: collectionCreatorPage.step2_otherChanged,
      };

      const params: Parameters<typeof FServiceAPI.Resource.saveVersionsDraft>[0] = {
        resourceId: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        draftData: draftData,
      };
      const {
        ret,
        errCode,
        data: data_draft,
      }: {
        ret: number;
        errCode: number;
        data: {
          resourceId: string;
          updateDate: string;
          draftData: IResourceCreateVersionDraftType;
        };
      } = yield call(FServiceAPI.Resource.saveVersionsDraft, params);
      if (ret !== 0 || errCode !== 0) {
        fMessage('草稿保存失败', 'error');
        return;
      }
      // if (payload.showSuccessTip) {
      //   fMessage('暂存草稿成功');
      // }
      yield call(FUtil.Tool.promiseSleep, 500);

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step2_draftSaving: false,
          step2_draftSaveTime: moment(data_draft.updateDate).format('YYYY-MM-DD HH:mm:ss'),
        },
      });
    },
    *onClick_step2_submitBtn(
      {}: OnClick_step2_submitBtn_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );

      // if (!collectionCreatorPage.step2_isCompleteAuthorization) {
      //   fMessage('依赖中存在未获取授权的资源', 'error');
      //   return;
      // }

      const authExcludedItems: {
        resourceId: string;
        excludedType: 'contractId' | 'policyId';
        excludedValue: string;
      }[] = collectionCreatorPage.step2_authExcludedItems.flatMap((r0) => {
        return r0.contracts.map((r1) => {
          return {
            resourceId: r0.resourceID,
            excludedType: 'contractId' as const,
            excludedValue: r1.contractID,
          };
        });
      });

      const params: Parameters<typeof FServiceAPI.Resource.updateCollection>[0] = {
        resourceId: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        inputAttrs: collectionCreatorPage.step2_systemProperties
          .filter((sp) => sp.type === 'additional')
          .map((sp) => {
            return {
              key: sp.key,
              value: sp.value,
            };
          }),
        customPropertyDescriptors: [
          ...collectionCreatorPage.step2_customProperties.map((i) => {
            return {
              type: 'readonlyText',
              key: i.key,
              name: i.name,
              remark: i.description,
              defaultValue: i.value,
            };
          }),
          ...collectionCreatorPage.step2_customConfigurations.map((i) => {
            const isInput: boolean = i.type === 'input';
            const options: string[] = i.select;
            return {
              type: isInput ? 'editableText' : 'select',
              key: i.key,
              name: i.name,
              remark: i.description,
              defaultValue: isInput ? i.input : options[0],
              // defaultValue: isInput ? i.input : '',
              candidateItems: isInput ? undefined : options,
            };
          }),
        ],
        description: '',
        catalogueProperty: collectionCreatorPage.step2_collectionItemsSetting,
        dependencies: collectionCreatorPage.step2_directDependencies.map((r) => {
          return {
            resourceId: r.id,
            versionRange: r.versionRange || '',
          };
        }),
        authExcludedItems: authExcludedItems,
        isMergeCatalogueDraft: collectionCreatorPage.step2_collectionItemsChanged ? 1 : 0,
      };

      const {
        ret,
        errCode,
        msg,
        data: data_result,
      } = yield call(FServiceAPI.Resource.updateCollection, params);

      if (ret !== 0 || errCode !== 0) {
        fMessage(msg, 'error');
      }

      // fMessage('发布成功', 'success');
    },
    *onClick_step3_addPolicyBtn(
      { payload }: OnClick_step3_addPolicyBtn_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      self._czc?.push(['_trackEvent', '授权信息页', '添加授权策略', '', 1]);
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );
      const parmas: Parameters<typeof fPolicyBuilder>[0] = {
        targetType: 'resource',
        alreadyUsedTexts: collectionCreatorPage.step3_policies.map<string>((ip) => {
          return ip.policyText;
        }),
        alreadyUsedTitles: collectionCreatorPage.step3_policies.map((ip) => {
          return ip.policyName;
        }),
        defaultValue: payload.defaultValue,
      };
      // console.log('*********************************************');
      const result: null | { title: string; text: string } = yield call(fPolicyBuilder, parmas);
      // console.log(result, 'resultsdifj;lskdjflk;jsdklfjklsdjlkfjklj');
      if (!result) {
        return;
      }
      const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
        resourceId: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        addPolicies: [
          {
            policyName: result.title,
            policyText: window.encodeURIComponent(result.text),
          },
        ],
      };
      const {
        ret,
        errCode,
        msg,
        data,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: any;
      } = yield call(FServiceAPI.Resource.update, params);

      if (ret !== 0 || errCode !== 0) {
        fMessage(msg, 'error');
        return;
      }

      const params1: Parameters<typeof FServiceAPI.Resource.info>[0] = {
        resourceIdOrName: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        isLoadPolicyInfo: 1,
        isTranslate: 1,
      };

      const {
        data: data_ResourceDetails,
      }: {
        ret: number;
        errCode: number;
        data: {
          resourceId: string;
          resourceName: string;
          policies: any[];
          baseUpcastResources: any[];
          status: 0 | 1;
        };
      } = yield call(FServiceAPI.Resource.info, params1);

      if (ret !== 0 || errCode !== 0) {
        return;
      }

      const policies: PolicyFullInfo_Type[] = data_ResourceDetails.policies || [];

      policies.reverse();

      policies.sort((a, b) => {
        return a.status === 1 && b.status === 0 ? -1 : 0;
      });
      // console.log(policies, 'policiesisdjf;lksdjfkljdskljl');
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step3_policies: policies,
        },
      });
    },
    *onClick_step3_submitBtn(
      {}: OnClick_step3_submitBtn_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step: 4,
        },
      });

      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );
      const params: Parameters<typeof FServiceAPI.Resource.info>[0] = {
        resourceIdOrName: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        isLoadPolicyInfo: 1,
        isTranslate: 1,
      };
      // console.log(params, 'params9iosdj;flkjlk lksdajf;lkjl');
      const {
        data: data_resourceInfo,
      }: {
        data: {
          userId: number;
          status: number;
          resourceId: string;
          resourceName: string;
          resourceTitle: string;
          resourceVersions: {
            version: string;
          }[];
          latestVersion: string;
          coverImages: string[];
          resourceType: string[];
          policies: PolicyFullInfo_Type[];
        };
      } = yield call(FServiceAPI.Resource.info, params);

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step4_resourceCover: data_resourceInfo.coverImages[0] || '',
        },
      });
    },
    *onChange_step1_resourceTitle(
      { payload }: OnChange_step1_resourceTitle_Action,
      { select, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step1_resourceTitle: payload.value,
          step1_resourceTitle_errorText:
            payload.value === ''
              ? // ? '请输入标题'
                FI18n.i18nNext.t('naming_convention_resource_title_required')
              : payload.value.length > 100
              ? '不超过100个字符'
              : '',
        },
      });
    },
    *onChange_step4_resourceCover(
      { payload }: OnChange_step4_resourceCover_Action,
      { select, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step4_resourceCover: payload.value,
          step4_dataIsDirty_count: collectionCreatorPage.step4_dataIsDirty_count + 1,
        },
      });
    },
    *onChange_step4_resourceLabels(
      { payload }: OnChange_step4_resourceLabels_Action,
      { select, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step4_resourceLabels: payload.value,
          step4_dataIsDirty_count: collectionCreatorPage.step4_dataIsDirty_count + 1,
        },
      });
    },
    *onClick_step4_preBtn({}: OnClick_step4_preBtn_Action, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step: 3,
        },
      });
    },
    *step4_Post_UpdateSetting(
      action: Step4_Post_UpdateSetting_Action,
      { select, call }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.setCollectRules>[0] = {
        resourceId: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        status: collectionCreatorPage.step4_updateSetting.automatic ? 1 : 0,
        conditionType: collectionCreatorPage.step4_updateSetting.conditionType === 'every' ? 1 : 2,
        filterConditions: collectionCreatorPage.step4_updateSetting.conditions.map((c) => {
          return {
            key: c.key,
            limitOperatorType: c.limitOperatorType,
            value: c.limitOperatorType === 'STARTS_WITH' ? c.value : c.value,
          };
        }),
      };

      yield call(FServiceAPI.Resource.setCollectRules, params);
    },
    *onClick_step4_submitBtn(
      {}: OnClick_step4_submitBtn_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionCreatorPage }: ConnectState = yield select(
        ({ collectionCreatorPage }: ConnectState) => ({
          collectionCreatorPage,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
        resourceId: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        tags: collectionCreatorPage.step4_resourceLabels,
        coverImages:
          collectionCreatorPage.step4_resourceCover !== ''
            ? [collectionCreatorPage.step4_resourceCover]
            : undefined,
        // resourceTitle: collectionCreatorPage.step4_resourceTitle,
        // status: 1,
        intro: collectionCreatorPage.step4_resourceIntroduction,
      };
      const {
        ret: ret_0,
        errCode: errCode_0,
        msg: msg_0,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: any;
      } = yield call(FServiceAPI.Resource.update, params);

      const params1: Parameters<typeof FServiceAPI.Resource.setCollectRules>[0] = {
        resourceId: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        // @ts-ignore
        serializeStatus: collectionCreatorPage.step4_updateSetting.isFinish ? 1 : 0,
        status: collectionCreatorPage.step4_updateSetting.automatic ? 1 : 0,
        conditionType: collectionCreatorPage.step4_updateSetting.conditionType === 'every' ? 1 : 2,
        filterConditions: collectionCreatorPage.step4_updateSetting.conditions.map((c) => {
          return {
            key: c.key,
            limitOperatorType: c.limitOperatorType,
            value: c.value,
          };
        }),
      };

      const {
        ret: ret_1,
        errCode: errCode_1,
        msg: msg_1,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: any;
      } = yield call(FServiceAPI.Resource.setCollectRules, params1);

      const params2: Parameters<typeof FServiceAPI.Resource.update>[0] = {
        resourceId: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        // tags: collectionCreatorPage.step4_resourceLabels,
        // coverImages: collectionCreatorPage.step4_resourceCover !== '' ? [collectionCreatorPage.step4_resourceCover] : undefined,
        // resourceTitle: collectionCreatorPage.step4_resourceTitle,
        status: 1,
        // intro: collectionCreatorPage.step4_resourceIntroduction,
      };
      const {
        ret: ret_2,
        errCode: errCode_2,
        msg: msg_2,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: any;
      } = yield call(FServiceAPI.Resource.update, params2);

      if (
        ret_0 !== 0 ||
        errCode_0 !== 0 ||
        ret_1 !== 0 ||
        errCode_1 !== 0 ||
        ret_2 !== 0 ||
        errCode_2 !== 0
      ) {
        if (ret_0 !== 0 || errCode_0 !== 0) {
          fMessage(msg_0, 'error');
        }

        if (ret_1 !== 0 || errCode_1 !== 0) {
          fMessage(msg_0, 'error');
        }

        if (ret_2 !== 0 || errCode_2 !== 0) {
          fMessage(msg_2, 'error');
        }
        return;
      }

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          step4_dataIsDirty_count: 0,
        },
      });
      setTimeout(() => {
        // history.push(FUtil.LinkTo.resourceCreateSuccess({
        //   resourceID: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        // }));
        // history.push(FUtil.LinkTo.collectionVersionInfo({
        //   collectionID: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
        // }));
        history.replace(
          FUtil.LinkTo.collectionCreateSuccess({
            collectionID: collectionCreatorPage.step1_createdResourceInfo?.resourceID || '',
          }),
        );
      }, 100);
    },
  },

  reducers: {
    change(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },
  subscriptions: {
    setup({ dispatch, history }: SubscriptionAPI) {},
  },
};

export default Model;
